import type { User } from "schemas/generated/user";

import React, { useCallback, useEffect, useRef, useState } from "react";

import { Alert } from "@replo/design-system/blocks/alert/Alert";
import { Button } from "@replo/design-system/components/button/Button";
import { Drawer } from "@replo/design-system/components/drawer/Drawer";
import { Modal } from "@replo/design-system/components/modal/Modal";
import {
  PopoverContent,
  PopoverDescription,
  PopoverFooter,
  PopoverHeader,
  PopoverRoot,
  PopoverTitle,
  PopoverTrigger,
} from "@replo/design-system/components/popover/Popover";
import { Progress } from "@replo/design-system/components/progress/Progress";
import { Textarea } from "@replo/design-system/components/textarea/Textarea";
import { toast } from "@replo/design-system/components/toast/Toast";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import useDefaultCustomDomain from "@v2/features/page-settings/useDefaultCustomDomain";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import {
  v2ShopDeploymentRouter,
  v2ShopDeploymentUtils,
} from "@v2/trpc/routes/v2ShopDeployment";
import { ArrowUpRight, ChevronDown, ChevronUp } from "lucide-react";
import { PuffLoader } from "react-spinners";
import { z } from "zod";

import {
  useDeploymentState,
  useDeploymentStateActions,
} from "./deploymentStateStore";
import { invalidateResourcesRelatedToDeployment } from "./invalidateResourcesRelatedToDeployment";

type TemporaryState = {
  type: "success" | "error" | null;
  details?: {
    type: "build" | "deploy";
    stdout: string;
    stderr: string;
  };
};

const DebugLogsAlert: React.FC<{
  details: { type: "build" | "deploy"; stdout: string; stderr: string };
}> = ({ details }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Alert
        variant="error"
        description="[Debug] Error publishing"
        action={{ text: "View logs", onClick: () => setIsOpen(true) }}
      />
      <Modal
        open={isOpen}
        onOpenChange={setIsOpen}
        title={`Debug Logs (${details.type})`}
        size="lg"
      >
        <div className="flex flex-col gap-2 w-full">
          <Textarea
            value={details.stdout}
            className="w-full"
            readOnly
            UNSAFE_className="h-[200px] font-mono text-2xs"
          />
          <Textarea
            value={details.stderr}
            className="w-full"
            readOnly
            UNSAFE_className="h-[200px] font-mono text-2xs"
          />
        </div>
      </Modal>
    </>
  );
};

const getPublishTitle = ({
  temporaryState,
  isPublishing,
  routePath,
  routeType,
}: {
  temporaryState: TemporaryState | null;
  isPublishing: boolean;
  routePath: string;
  routeType: "funnel" | "page";
}): string => {
  if (temporaryState?.type === "success") {
    return `${routeType === "funnel" ? "Funnel" : "Page"} Published Successfully`;
  }
  if (temporaryState?.type === "error") {
    return "Publish Unsuccessful";
  }
  if (isPublishing) {
    return routePath ? `Publishing ${routePath}` : "Publishing your page...";
  }
  return "Publish";
};

const getPublishDescription = ({
  temporaryState,
  isPublishing,
  routePath,
  fileIdsToPublish,
}: {
  temporaryState: TemporaryState | null;
  isPublishing: boolean;
  routePath: string;
  fileIdsToPublish: string[];
}): string | undefined => {
  if (temporaryState?.type === "success") {
    return routePath
      ? `${routePath} is now live.`
      : `Your ${fileIdsToPublish.length > 1 ? "pages are" : "page is"} now live.`;
  }
  if (temporaryState?.type === "error") {
    return "We're on it. Our engineers have been notified. Try again soon, or reach us at support@replo.app.";
  }
  if (isPublishing) {
    return "Feel free to leave this tab.";
  }
  return undefined;
};

const PublishDialogContent = ({
  isPublishing,
  temporaryState,
  publishedSiteUrl,
  siteUrlWithoutProtocol,
  isViewableLive,
  faviconUrl,
  simulatedProgressPercentage,
  user,
}: {
  isPublishing: boolean;
  temporaryState: TemporaryState | null;
  publishedSiteUrl: string;
  siteUrlWithoutProtocol: string;
  isViewableLive: boolean;
  faviconUrl?: string;
  simulatedProgressPercentage: number;
  user: User | undefined;
}) => {
  if (isPublishing || temporaryState) {
    return (
      <div className="flex flex-col gap-2">
        {user?.isSuperuser &&
          temporaryState?.type === "error" &&
          temporaryState.details && (
            <DebugLogsAlert details={temporaryState.details} />
          )}
        <PublishingLoadingContent
          status={temporaryState?.type ?? null}
          publishedSiteUrl={publishedSiteUrl}
          siteUrlWithoutProtocol={siteUrlWithoutProtocol}
          isViewableLive={isViewableLive}
          faviconUrl={faviconUrl}
          simulatedProgressPercentage={simulatedProgressPercentage}
        />
      </div>
    );
  }

  return (
    <LinkToPublishedPage
      siteUrl={publishedSiteUrl}
      siteUrlWithoutProtocol={siteUrlWithoutProtocol}
      isViewableLive={isViewableLive}
      faviconUrl={faviconUrl}
    />
  );
};

export const PublishButton = ({
  routeType,
  fileIdsToPublish,
  publishedSiteUrl,
  isViewableLive,
}: {
  routeType: "funnel" | "page";
  fileIdsToPublish: string[];
  publishedSiteUrl: string;
  isViewableLive: boolean;
}) => {
  const isMobile = useIsMobile();
  const { user } = useCurrentUser();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { project } = useCurrentProjectContext();
  const logEvent = useLogAnalytics();

  const defaultCustomDomain = useDefaultCustomDomain();
  const showAddDomainButton = !Boolean(defaultCustomDomain);

  const deploymentState = useDeploymentState(fileIdsToPublish);
  const { setPublishing, setSuccess, setError, clearDeployment } =
    useDeploymentStateActions();
  const [temporaryState, setTemporaryState] = useState<TemporaryState | null>(
    null,
  );

  const { data: pendingDeployments } =
    v2ShopDeploymentRouter.findDeployments.useQuery(
      { projectId: project?.id ?? "", deployStatus: "PENDING" },
      { enabled: Boolean(project?.id) },
    );

  const { mutate: deploy } = v2ShopDeploymentRouter.deploy.useMutation({
    onMutate: async ({ projectId, fileIdsToPublish }) => {
      await v2ShopDeploymentUtils.findDeployments.cancel({
        projectId,
        deployStatus: "PENDING",
      });

      const previousPendingDeployments =
        v2ShopDeploymentUtils.findDeployments.getData({
          projectId,
          deployStatus: "PENDING",
        });

      const filesPublished = fileIdsToPublish.map((fileId) => ({
        id: fileId,
        createdAt: new Date(),
        updatedAt: new Date(),
        deletedAt: null,
        name: "Publishing...",
        fileType: "page",
        path: null,
        projectId,
        pageRoute: null,
        pageMetadataTitle: null,
        pageMetadataDescription: null,
        pageMetadataOpenGraphImageUrl: null,
        pageMetadataNoIndex: false,
        content: "",
        lastPublishedFileVersionId: null,
      }));

      const optimisticDeployment = {
        id: `optimistic-${Date.now()}`,
        createdAt: new Date(),
        projectId,
        deployStatus: "PENDING" as const,
        filesPublished,
      };

      v2ShopDeploymentUtils.findDeployments.setData(
        { projectId, deployStatus: "PENDING" },
        (oldData) => {
          if (!oldData) {
            return [optimisticDeployment];
          }
          return [optimisticDeployment, ...oldData];
        },
      );

      return { previousPendingDeployments };
    },
    onSettled: (_data, _error, { projectId }) => {
      void v2ShopDeploymentUtils.findDeployments.invalidate({
        projectId,
        deployStatus: "PENDING",
      });
    },
    onSuccess: (_data, variables) => {
      setSuccess(variables.fileIdsToPublish);
      setTemporaryState({ type: "success" });

      toast.success({
        title: `${routeType === "funnel" ? "Funnel" : "Page"} Successfully Published`,
        description: `Your updated ${routeType === "funnel" ? "pages are" : "page is"} now live.`,
        action: {
          label: "View Live",
          onClick: () => {
            window.open(publishedSiteUrl, "_blank");
          },
        },
      });

      invalidateResourcesRelatedToDeployment();
    },
    onError: (error, variables, context) => {
      // Check if this is a V2EntitlementRejected error - if so, do nothing
      // The error will be handled by the default frontend error handling system
      if (
        error.data?.code === "FORBIDDEN" &&
        error.data?.userFacingDetails?.key?.startsWith(
          "v2.entitlements.insufficient",
        )
      ) {
        setIsPopoverOpen(false);
        return;
      }

      const details = z
        .object({
          type: z.enum(["build", "deploy"]),
          stdout: z.string(),
          stderr: z.string(),
        })
        .safeParse(error.shape?.data?.details)?.data;

      console.error("Publish failed", details);
      setError(variables.fileIdsToPublish);
      setTemporaryState({ type: "error", details });

      if (context?.previousPendingDeployments) {
        v2ShopDeploymentUtils.findDeployments.setData(
          { projectId: variables.projectId, deployStatus: "PENDING" },
          context.previousPendingDeployments,
        );
      }
    },
  });

  const pendingDeploymentForThisPage = pendingDeployments?.find(
    (deployment) => {
      return deployment.filesPublished.some((file) => {
        return fileIdsToPublish.includes(file.id);
      });
    },
  );
  const hasPendingDeployment = Boolean(pendingDeploymentForThisPage);

  const publishStartTime =
    deploymentState?.status === "publishing"
      ? deploymentState.startTime
      : undefined;

  const isThisPagePublishing =
    deploymentState?.status === "publishing" || hasPendingDeployment;

  const simulatedProgressPercentage = useSimulatedProgress({
    isActive: isThisPagePublishing && !temporaryState,
    targetDurationMs: 75000,
    updateFrequencyMs: 500,
    startTime: publishStartTime,
  });

  if (!project) {
    return null;
  }

  const siteUrlWithoutProtocol = publishedSiteUrl.replace("https://", "");
  const routePath = new URL(publishedSiteUrl).pathname;
  const faviconUrl =
    project.shopMetadataLightModeFaviconUrl ??
    project.shopMetadataDarkModeFaviconUrl ??
    undefined;

  const handlePublish = () => {
    setPublishing(fileIdsToPublish, Date.now(), {
      routeType,
      publishedSiteUrl,
    });
    deploy({
      projectId: project.id,
      fileIdsToPublish,
      fileIdsToUnpublish: [],
    });
    logEvent("editor.nav.publish.click");
  };

  const handlePopoverOpenChange = (open: boolean) => {
    if (!open && temporaryState && !isThisPagePublishing) {
      clearDeployment(fileIdsToPublish);
      setTemporaryState(null);
    }
    setIsPopoverOpen(open);
  };

  const title = getPublishTitle({
    temporaryState,
    isPublishing: isThisPagePublishing,
    routePath,
    routeType,
  });
  const description = getPublishDescription({
    temporaryState,
    isPublishing: isThisPagePublishing,
    routePath,
    fileIdsToPublish,
  });

  const showFooter = !isThisPagePublishing && !temporaryState;
  const footer = showFooter && (
    <PublishPopoverFooter
      isPublishing={isThisPagePublishing}
      handlePublish={handlePublish}
      showAddDomainButton={showAddDomainButton}
    />
  );

  if (isMobile) {
    return (
      <>
        <Button
          variant="primary"
          size="base"
          onClick={() => setIsPopoverOpen(true)}
        >
          Publish
        </Button>
        <Drawer
          open={isPopoverOpen}
          onOpenChange={handlePopoverOpenChange}
          title={isThisPagePublishing || temporaryState ? title : undefined}
          description={
            isThisPagePublishing || temporaryState ? description : undefined
          }
          footer={footer}
        >
          <PublishDialogContent
            isPublishing={isThisPagePublishing}
            temporaryState={temporaryState}
            publishedSiteUrl={publishedSiteUrl}
            siteUrlWithoutProtocol={siteUrlWithoutProtocol}
            isViewableLive={isViewableLive}
            faviconUrl={faviconUrl}
            simulatedProgressPercentage={simulatedProgressPercentage}
            user={user}
          />
        </Drawer>
      </>
    );
  }

  return (
    <PopoverRoot open={isPopoverOpen} onOpenChange={handlePopoverOpenChange}>
      <PopoverTrigger
        render={
          <Button
            variant="primary"
            size="base"
            endEnhancer={isPopoverOpen ? <ChevronUp /> : <ChevronDown />}
          />
        }
      >
        Publish
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={8}
        showCloseButton={false}
        className="w-96"
      >
        {(isThisPagePublishing || temporaryState) && (
          <PopoverHeader>
            <PopoverTitle>{title}</PopoverTitle>
            {description && (
              <PopoverDescription>{description}</PopoverDescription>
            )}
          </PopoverHeader>
        )}
        <PublishDialogContent
          isPublishing={isThisPagePublishing}
          temporaryState={temporaryState}
          publishedSiteUrl={publishedSiteUrl}
          siteUrlWithoutProtocol={siteUrlWithoutProtocol}
          isViewableLive={isViewableLive}
          faviconUrl={faviconUrl}
          simulatedProgressPercentage={simulatedProgressPercentage}
          user={user}
        />
        {footer && <PopoverFooter>{footer}</PopoverFooter>}
      </PopoverContent>
    </PopoverRoot>
  );
};

const PublishingLoadingContent = ({
  status,
  publishedSiteUrl,
  siteUrlWithoutProtocol,
  isViewableLive,
  faviconUrl,
  simulatedProgressPercentage,
}: {
  status: "success" | "error" | null;
  publishedSiteUrl: string;
  siteUrlWithoutProtocol: string;
  isViewableLive: boolean;
  faviconUrl?: string;
  simulatedProgressPercentage: number;
}) => {
  // Use simulated progress when still loading, 100% when successful
  const displayProgress =
    status === "success" || status === "error"
      ? 100
      : simulatedProgressPercentage;
  const displayPercentage = Math.round(displayProgress);

  return (
    <div className="flex flex-col items-center">
      {status === "success" && (
        <div className="flex flex-col items-center gap-4 w-full">
          <div className="w-72 h-32 flex items-center justify-center">
            <img
              src="/images/shops/successful-publish.png"
              alt="Successful publish"
              className="w-64"
            />
          </div>
          <LinkToPublishedPage
            siteUrl={publishedSiteUrl}
            siteUrlWithoutProtocol={siteUrlWithoutProtocol}
            isViewableLive={isViewableLive}
            faviconUrl={faviconUrl}
          />
        </div>
      )}
      {status === "error" && (
        <div className="relative w-48 h-32">
          <img
            src="/images/shops/failed-publish.png"
            alt="Failed publish"
            className="absolute top-1/2 left-1/2 w-80 -translate-x-[50%] -translate-y-[50%] z-10"
          />
        </div>
      )}
      {!status && (
        <div className="relative w-32 h-32">
          <img
            src="/images/shops/Globe.png"
            alt="Globe"
            className="absolute top-1/2 left-1/2 w-16 h-16 -translate-x-[56%] -translate-y-[56%] z-10"
          />
          <PuffLoader color="#2563EB" size={120} speedMultiplier={0.75} />
        </div>
      )}

      {!status && (
        <div className="w-full flex gap-3 items-center">
          <Progress
            value={displayProgress}
            className="h-2"
            UNSAFE_indicatorClassName="transition-all duration-500 ease-out"
          />
          <span className="text-text-xs font-semibold text-sm">
            {displayPercentage}%
          </span>
        </div>
      )}
    </div>
  );
};

const LinkToPublishedPage = ({
  siteUrl,
  siteUrlWithoutProtocol,
  isViewableLive,
  faviconUrl,
}: {
  siteUrl: string;
  siteUrlWithoutProtocol: string;
  isViewableLive: boolean;
  faviconUrl?: string;
}) => {
  return (
    <div className="flex flex-col gap-4 p-3 border-border border-0.5 rounded-lg bg-sheet w-full">
      <Tooltip content={siteUrlWithoutProtocol} triggerAsChild>
        <button
          onClick={
            isViewableLive
              ? () => {
                  window.open(siteUrl, "_blank");
                }
              : undefined
          }
          className={twMerge(
            "w-full truncate justify-start flex items-center gap-1.5 bg-transparent border-none cursor-pointer text-left",
            isViewableLive ? "text-default" : "text-muted",
            !isViewableLive && "cursor-not-allowed",
          )}
          disabled={!isViewableLive}
        >
          <img
            src={
              faviconUrl ??
              "https://shops-metadata.replocdn.com/default-favicon-light-mode.ico"
            }
            alt={faviconUrl ? "Site favicon" : "Default site favicon"}
            className="w-4 h-4 flex-shrink-0"
          />
          <span
            className="truncate hover:underline text-sm text-left"
            style={{ direction: "rtl" }}
          >
            {siteUrlWithoutProtocol}
          </span>
          {isViewableLive && <ArrowUpRight className="w-4 h-4 flex-shrink-0" />}
        </button>
      </Tooltip>
    </div>
  );
};

const PublishPopoverFooter = ({
  isPublishing,
  handlePublish,
  showAddDomainButton,
}: {
  isPublishing: boolean;
  handlePublish: () => void;
  showAddDomainButton: boolean;
}) => {
  const { project } = useCurrentProjectContext();

  const handleAddDomain = () => {
    if (project?.id) {
      // NOTE (Gabe 2025-07-24): We don't useNavigate here because we explicitly
      // want this to be a server loaded navigation so that Cross-Origin headers
      // are not used (they break Entri's oauth flow). Not a big deal either
      // way, using a direct nav here makes for a slightly more seamless UX. See
      // more in App.tsx's shouldTriggerServerLoadedNavigation.
      window.location.href = `/shops/${project.id}/settings/shop/general`;
    }
  };

  return (
    <div className="flex flex-col md:flex-row gap-3">
      {showAddDomainButton && (
        <Button
          variant="secondary"
          size="base"
          onClick={handleAddDomain}
          className="w-full md:w-fit"
        >
          Add Domain
        </Button>
      )}
      <Button
        variant="primary"
        size="base"
        onClick={handlePublish}
        loading={isPublishing}
        className="w-full md:w-fit"
      >
        Publish
      </Button>
    </div>
  );
};

// Simple easing function - starts fast, slows down at the end
const easeOut = (t: number): number => {
  return 1 - Math.pow(1 - t, 2);
};

const useSimulatedProgress = ({
  isActive,
  targetDurationMs = 75000,
  updateFrequencyMs = 250,
  startTime,
}: {
  isActive: boolean;
  targetDurationMs?: number;
  updateFrequencyMs?: number;
  startTime?: number;
}) => {
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const previousProgressRef = useRef(0);

  const updateProgress = useCallback(() => {
    if (!startTime || !isActive) {
      return;
    }

    const elapsed = Date.now() - startTime;
    const normalizedTime = Math.min(elapsed / targetDurationMs, 1);

    // Simple progression - fast at first, slower at the end
    const easedProgress = easeOut(normalizedTime);

    // Add small random bump (0-2%) to make it feel less predictable
    const randomBump = Math.random() * 0.02;
    const calculatedProgress = Math.min((easedProgress + randomBump) * 98, 98); // Cap at 98%

    // Ensure progress never decreases
    const newProgress = Math.max(
      calculatedProgress,
      previousProgressRef.current,
    );
    previousProgressRef.current = newProgress;
    setProgress(newProgress);

    // Stop the interval when we reach the end
    if (normalizedTime >= 1 && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [isActive, targetDurationMs, startTime]);

  useEffect(() => {
    if (isActive) {
      // NOTE (Andrew, 2026-01-26, REPL-19619): Calculate initial progress from elapsed time (for returning to page mid-publish)
      if (startTime) {
        const elapsed = Date.now() - startTime;
        const normalizedTime = Math.min(elapsed / targetDurationMs, 1);
        const initialProgress = Math.min(easeOut(normalizedTime) * 98, 98);
        previousProgressRef.current = initialProgress;
        setProgress(initialProgress);
      } else {
        previousProgressRef.current = 0;
        setProgress(0);
      }
      intervalRef.current = setInterval(updateProgress, updateFrequencyMs);
    } else {
      previousProgressRef.current = 0;
      setProgress(0);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [
    isActive,
    updateProgress,
    updateFrequencyMs,
    startTime,
    targetDurationMs,
  ]);

  return progress;
};
